import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { itToast as toast } from "../../utils/infinityToast";
import { CheckCircle2, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CountrySelect, StateCombobox } from "@/components/shared/CountryStateFields";
import { cn } from "@/lib/utils";
import StripePayment from "@/components/store/StripePayment";
import { getImageUrl } from "../../utils/imageUtils";
import {
  isValidPostalCode, isValidUsPhone, isValidEmail, digitsOnly, sanitizePostalCode, postalCodeMaxLen,
  PHONE_ERROR, ZIP_ERROR, EMAIL_ERROR, PHONE_MAX_DIGITS,
} from "../../utils/validation";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const emptyAddr = { firstname: "", lastname: "", phone: "", address: "", city: "", country: "US", state: "", zipcode: "" };
const NEW_ADDR = "new"; // sentinel for "not one of the saved addresses"
const ADDR_FIELDS = Object.keys(emptyAddr);

/**
 * Checkout (TR-9 report 2) — a fork of the edisplayroom 3-step accordion, re-themed
 * to the IT palette: (1) Delivery Address, (2) Delivery Options, (3) inline Stripe
 * on the same page. Right "Your Order" summary shows item thumbnails + totals.
 */
export default function InfinityCheckout() {
  const { http, isAuthed, cart, clearCart, customer, addresses, saveAddress, removeItem, theme, stripePublishableKey, basePath, hasEdisplay, unlockAllProducts } = useInfinityCustomer();
  const navigate = useNavigate();

  const primary = theme.primary_color || "#2E72BC";

  const [activeStep, setActiveStep] = useState(1);
  const [form, setForm] = useState({ contact_email: "", shipping_address: { ...emptyAddr } });
  const [errors, setErrors] = useState({});
  const [charges, setCharges] = useState({ engraving_charge: 0, shipping_methods: [], free_shipping_threshold: null });
  const [shippingId, setShippingId] = useState("");
  const [saveToBook, setSaveToBook] = useState(false);
  /* Which saved address is in the form: an address id, or NEW_ADDR for one the
   * family member is typing from scratch. `addrTouched` flips as soon as they
   * edit a field of a saved address, so the form stops claiming to be that
   * address and the "save to my address book" offer comes back. */
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [addrTouched, setAddrTouched] = useState(false);

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [serverTotal, setServerTotal] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null); // { order_id, order_number }
  const [paid, setPaid] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /* Set when Stripe took the payment but our server had not recorded it before the
   * poll gave up. The order IS paid; the background sweep finishes the job. */
  const [confirmPending, setConfirmPending] = useState(false);
  const [payFailure, setPayFailure] = useState(null);

  useEffect(() => {
    if (!isAuthed) { navigate(`${basePath}/login`); return; }
    if (cart.length === 0) { navigate(`${basePath}/shop`); return; }
    http.get("/charges").then((r) => {
      setCharges(r.data || {});
      const methods = r.data?.shipping_methods || [];
      if (methods.length) setShippingId(methods[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Autofill from the pin-session customer + saved default address (TR-9 2.5).
  // Both arrive asynchronously (the customer is rehydrated from /case on a hard
  // refresh, the address book from /addresses), so this re-runs when they land
  // and only ever fills fields the family member hasn't typed into. The API
  // already strips the synthetic guest email/phone, so nothing fake gets filled.
  useEffect(() => {
    const def = (addresses || []).find((a) => a.is_default) || (addresses || [])[0];
    if (!customer && !def) return;

    const [fn, ...rest] = (customer?.name || "").split(" ");
    const suggested = {
      firstname: def?.firstname || fn || "",
      lastname: def?.lastname || rest.join(" ") || "",
      phone: def?.phone || customer?.phone || "",
      address: def?.address || "",
      city: def?.city || "",
      state: def?.state || "",
      zipcode: def?.zipcode || "",
    };

    setForm((f) => {
      const next = { ...f, shipping_address: { ...f.shipping_address } };
      if (!next.contact_email) next.contact_email = customer?.email || "";
      Object.entries(suggested).forEach(([k, v]) => {
        if (v && !next.shipping_address[k]) next.shipping_address[k] = v;
      });
      if (def?.country && next.shipping_address.country === emptyAddr.country) {
        next.shipping_address.country = def.country;
      }
      return next;
    });
    // The book arrives after the first render, so highlight whichever address
    // the autofill above just used.
    setSelectedAddrId((cur) => cur ?? (def?.id ? def.id : null));
  }, [customer, addresses]);

  useEffect(() => {
    if (stripePublishableKey && !stripePromise) setStripePromise(loadStripe(stripePublishableKey));
  }, [stripePublishableKey, stripePromise]);

  // The thank-you screen used to auto-continue to Home after 3.5s. It now carries
  // its own actions (including "see our full range"), and a CTA that disappears
  // mid-read is worse than none — so the family leaves when they choose to.

  const setAddr = (patch) => {
    setAddrTouched(true);
    setForm((f) => ({ ...f, shipping_address: { ...f.shipping_address, ...patch } }));
  };

  // Picking a saved address replaces the whole shipping block — unlike the
  // autofill effect, which only ever fills blanks — and drops the field errors
  // raised against whatever was there before.
  const pickAddress = (a) => {
    if (!a) return;
    const next = { ...emptyAddr };
    ADDR_FIELDS.forEach((k) => { if (a[k]) next[k] = a[k]; });
    setForm((f) => ({ ...f, shipping_address: next }));
    setErrors((e) => {
      const kept = { ...e };
      ADDR_FIELDS.forEach((k) => delete kept[`shipping_address.${k}`]);
      return kept;
    });
    setSelectedAddrId(a.id);
    setAddrTouched(false);
    setSaveToBook(false);
  };

  const startNewAddress = () => {
    setForm((f) => ({ ...f, shipping_address: { ...emptyAddr } }));
    setErrors((e) => {
      const kept = { ...e };
      ADDR_FIELDS.forEach((k) => delete kept[`shipping_address.${k}`]);
      return kept;
    });
    setSelectedAddrId(NEW_ADDR);
    setAddrTouched(false);
  };

  const addrLine = (a) => [a.address, a.city, [a.state, a.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const addrName = (a) => [a.firstname, a.lastname].filter(Boolean).join(" ");

  const unitPrice = (it) => Number(it.product.display_price ?? it.product.base_price ?? 0) + Number(it.customization_total || 0);

  const shippingFor = (method, subtotal) => {
    if (!method) return 0;
    if (charges.free_shipping_threshold && subtotal >= charges.free_shipping_threshold) return 0;
    const tiers = method.tiers || [];
    const tier = tiers.find((t) => subtotal >= t.min_value && subtotal < t.max_value);
    return Number(tier ? tier.shipping_cost : (tiers.length ? tiers[tiers.length - 1].shipping_cost : 0)) || 0;
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const it of cart) {
      subtotal += unitPrice(it) * it.quantity;
      subtotal += (it.engraving_lines?.length || 0) * (charges.engraving_charge || 0) * it.quantity;
    }
    const method = (charges.shipping_methods || []).find((s) => s.id === shippingId);
    const shipping = shippingFor(method, subtotal);
    return { subtotal, shipping, total: subtotal + shipping };
  }, [cart, charges, shippingId]); // eslint-disable-line

  const validateStep1 = () => {
    const e = {};
    const a = form.shipping_address;
    if (!isValidEmail(form.contact_email)) e["contact_email"] = EMAIL_ERROR;
    ["firstname", "lastname", "address", "city", "state", "zipcode"].forEach((k) => { if (!a[k]) e[`shipping_address.${k}`] = "Required"; });
    // Both checks count digits rather than characters — the old phone rule was
    // `a.phone.length < 10`, which let "(555) 12" through on formatting alone.
    if (!isValidUsPhone(a.phone)) e["shipping_address.phone"] = PHONE_ERROR;
    if (a.zipcode && !isValidPostalCode(a.zipcode, a.country)) {
      e["shipping_address.zipcode"] = a.country === "US" ? ZIP_ERROR : "Enter a valid postal code";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Saving the address is a convenience, never a blocker — a failed PUT must not
  // stop the family member from checking out.
  const proceedFromAddress = async () => {
    if (!validateStep1()) return;
    if (saveToBook) {
      try {
        // saveAddress appends and returns the whole book, so the new entry —
        // the one to highlight in the picker — is the last one back.
        const book = await saveAddress({ ...form.shipping_address, is_default: addresses.length === 0 });
        const added = (book || [])[(book || []).length - 1];
        if (added?.id) { setSelectedAddrId(added.id); setAddrTouched(false); }
        setSaveToBook(false);
      } catch {
        toast.error("Could not save the address, but you can still continue.");
      }
    }
    setActiveStep(2);
  };

  const goToPayment = async () => {
    if (!shippingId) return toast.error("Select a delivery option");
    setProcessing(true);
    try {
      const a = form.shipping_address;
      const { data } = await http.post("/place", {
        items: cart.map((it) => ({
          product_id: it.product.product_id,
          quantity: it.quantity,
          color: it.color,
          engraving_lines: it.engraving_lines,
          custom_image_url: it.custom_image_url,
          customizations: it.customizations,
        })),
        shipping_address: a,
        shipping_method_id: shippingId,
        contact_email: form.contact_email,
        customer_name: `${a.firstname} ${a.lastname}`.trim() || customer?.name,
      });
      setClientSecret(data.clientSecret);
      setServerTotal(data.total);
      setPlacedOrder({ order_id: data.order_id, order_number: data.order_number });
      setActiveStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not start checkout");
    } finally {
      setProcessing(false);
    }
  };

  /* Stripe has taken the card payment. Ask our server to record it before showing the
   * thank-you, exactly as the eDisplayroom success page does (CheckoutSuccess.jsx):
   * without this the order only settles if the Stripe webhook arrives, and while that
   * webhook was undelivered every family order sat unpaid with no commission booked
   * for the funeral home (TR-23). Same cadence as eDisplayroom: 15 tries, 2s apart. */
  const onPaid = async () => {
    clearCart();
    setConfirming(true);

    const orderId = placedOrder?.order_id;
    if (!orderId) { setPaid(true); setConfirming(false); return; }

    for (let attempt = 0; attempt < 15; attempt++) {
      try {
        const { data } = await http.post(`/orders/${orderId}/confirm`);
        if (data?.payment_status === "paid") {
          setPaid(true);
          setConfirming(false);
          return;
        }
        if (data?.payment_status === "failed" || data?.payment_status === "expired") {
          setPayFailure(data.failure_reason || "Your payment could not be completed.");
          setConfirming(false);
          return;
        }
      } catch {
        // Transient — keep trying; the sweep is the final backstop either way.
      }
      await new Promise((r) => setTimeout(r, 2000));
    }

    // Never strand the family on a spinner: the card cleared, so show the thank-you
    // and say the confirmation is still catching up.
    setConfirmPending(true);
    setPaid(true);
    setConfirming(false);
  };
  /* Stripe refused the card. The order row already exists — it is created before the
   * PaymentIntent so the intent has something to reference — so tell the server, or it
   * sits at payment_status 'pending' forever and shows up in the funeral home's panel as
   * a received order (TR-25 item 3). `/confirm` re-reads the intent from Stripe and is
   * the authority on the outcome; we never let the browser assert "this failed". */
  const onDeclined = async () => {
    const orderId = placedOrder?.order_id;
    if (!orderId) return;
    try {
      const { data } = await http.post(`/orders/${orderId}/confirm`);
      if (data?.payment_status === "failed" || data?.payment_status === "expired") {
        setPayFailure(data.failure_reason || "Your payment could not be completed.");
      }
    } catch {
      // The card error toast already told them. The sweep marks the order later.
    }
  };

  const goToOrder = () => {
    navigate(placedOrder?.order_id ? `${basePath}/home#order-${placedOrder.order_id}` : `${basePath}/home#orders`);
  };

  // Payment cleared with Stripe; waiting for our server to record it.
  if (confirming) {
    return (
      <div className="max-w-lg mx-auto text-center py-24 space-y-4">
        <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: primary }} />
        <h1 className="text-xl font-bold text-slate-900">Confirming your payment…</h1>
        <p className="text-slate-500">This only takes a moment. Please don't close this page.</p>
      </div>
    );
  }

  // Stripe reported the payment as failed or cancelled.
  if (payFailure) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 space-y-5">
        <h1 className="text-2xl font-bold text-slate-900">Payment not completed</h1>
        <p className="text-slate-500">{payFailure}</p>
        <Button className="text-white px-8 h-12 rounded-xl" style={{ backgroundColor: primary }} onClick={() => navigate(`${basePath}/shop`)}>
          Back to the catalogue
        </Button>
      </div>
    );
  }

  // Thank-you screen after a successful payment. Terminal — the family picks where
  // to go next rather than being redirected out from under the CTAs.
  if (paid) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-5">
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundColor: primary }}>
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Thank you for your order!</h1>
        <p className="text-slate-500">
          {placedOrder?.order_number ? <>Your order <span className="font-semibold text-slate-700">{placedOrder.order_number}</span> has been placed.</> : "Your order has been placed."}
          {" "}A confirmation will follow by email.
        </p>
        {confirmPending ? (
          <p className="text-sm text-slate-400">
            Your payment went through and we're still recording it — your order will show as paid shortly.
          </p>
        ) : null}
        <Button className="text-white px-8 h-12 rounded-xl" style={{ backgroundColor: primary }} onClick={goToOrder}>
          View your order
        </Button>

        {/* Only the funeral homes that sell eDisplayroom have a full range to show.
            This CTA is the single entry point to it: taking it also unhides the
            "All Products" tab in the header for the rest of the session (TR-22). */}
        {hasEdisplay ? (
          <div className="pt-4 space-y-2">
            <p className="text-sm text-slate-500">There's more to remember them by.</p>
            <Button
              variant="outline"
              className="px-8 h-12 rounded-xl"
              onClick={() => {
                unlockAllProducts();
                navigate(`${basePath}/shop?channel=all`);
              }}
            >
              See our full range of products
            </Button>
          </div>
        ) : null}

        <div>
          <button onClick={() => navigate(`${basePath}/home`)} className="text-sm text-slate-500 hover:text-slate-800">
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  const err = (k) => errors[k];
  const inputCls = (k) => cn("h-12 bg-slate-50 border-transparent focus:bg-white transition-all rounded-xl", err(k) && "border-red-500");
  const stepHeader = (n, title, node) => (
    <div className={cn("flex items-center gap-4 p-5 border-b transition-colors", activeStep === n ? "bg-white border-slate-100" : "bg-slate-50 border-transparent text-slate-500")}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white"
        style={{ backgroundColor: activeStep >= n ? primary : "#cbd5e1" }}>{n}</div>
      <h3 className="font-bold text-lg">{node || title}</h3>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* Steps */}
      <div className="lg:col-span-8 space-y-6">
        {/* Step 1 — Delivery Address */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          {stepHeader(1, "Delivery Address")}
          {activeStep === 1 ? (
            <div className="p-6 space-y-6">
              {/* Saved address book — the family member picks one and the form
                  below fills in. Hidden entirely before the first save, so a
                  first-time buyer sees the plain form. */}
              {addresses.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">Saved Addresses</h4>
                  <RadioGroup
                    value={selectedAddrId ?? ""}
                    onValueChange={(v) => (v === NEW_ADDR ? startNewAddress() : pickAddress(addresses.find((a) => a.id === v)))}
                    className="space-y-2"
                  >
                    {addresses.map((a) => {
                      const sel = selectedAddrId === a.id;
                      return (
                        <label key={a.id} className={cn("flex items-start gap-3 rounded-xl border p-4 cursor-pointer", sel && "ring-1")}
                          style={sel ? { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` } : undefined}>
                          <RadioGroupItem value={a.id} className="mt-1" />
                          <span className="text-sm">
                            <span className="font-semibold text-slate-800">{addrName(a) || addrLine(a)}</span>
                            {a.is_default ? <span className="ml-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: primary }}>Default</span> : null}
                            <span className="block text-slate-500">{addrLine(a)}</span>
                            {a.phone ? <span className="block text-slate-400 text-xs">{a.phone}</span> : null}
                          </span>
                        </label>
                      );
                    })}
                    <label className={cn("flex items-center gap-3 rounded-xl border p-4 cursor-pointer", selectedAddrId === NEW_ADDR && "ring-1")}
                      style={selectedAddrId === NEW_ADDR ? { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` } : undefined}>
                      <RadioGroupItem value={NEW_ADDR} />
                      <span className="text-sm font-semibold text-slate-800">Use a new address</span>
                    </label>
                  </RadioGroup>
                </div>
              ) : null}

              <div>
                <h4 className="font-semibold text-slate-800 mb-3">Contact Information</h4>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email address *</Label>
                  <Input className={inputCls("contact_email")} placeholder="you@example.com" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-3">Shipping Details</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="First name *" ek="shipping_address.firstname" err={err} cls={inputCls}>
                    <Input className={inputCls("shipping_address.firstname")} value={form.shipping_address.firstname} onChange={(e) => setAddr({ firstname: e.target.value })} />
                  </Field>
                  <Field label="Last name *" ek="shipping_address.lastname" err={err} cls={inputCls}>
                    <Input className={inputCls("shipping_address.lastname")} value={form.shipping_address.lastname} onChange={(e) => setAddr({ lastname: e.target.value })} />
                  </Field>
                </div>
                <div className="mt-4 space-y-4">
                  <Field label="Phone *" ek="shipping_address.phone" err={err} cls={inputCls}>
                    <Input className={inputCls("shipping_address.phone")} value={form.shipping_address.phone}
                      type="tel" inputMode="tel" maxLength={PHONE_MAX_DIGITS}
                      onChange={(e) => setAddr({ phone: digitsOnly(e.target.value, PHONE_MAX_DIGITS) })} />
                  </Field>
                  <Field label="Address *" ek="shipping_address.address" err={err} cls={inputCls}>
                    <Input className={inputCls("shipping_address.address")} value={form.shipping_address.address} onChange={(e) => setAddr({ address: e.target.value })} />
                  </Field>
                  <Field label="Town / City *" ek="shipping_address.city" err={err} cls={inputCls}>
                    <Input className={inputCls("shipping_address.city")} value={form.shipping_address.city} onChange={(e) => setAddr({ city: e.target.value })} />
                  </Field>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Country *</Label>
                      <CountrySelect
                        value={form.shipping_address.country}
                        onChange={(v) => setAddr({ country: v, state: "" })}
                        className="h-12 bg-slate-50 border-transparent rounded-xl"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">State *</Label>
                      <StateCombobox
                        country={form.shipping_address.country}
                        value={form.shipping_address.state}
                        onChange={(v) => setAddr({ state: v })}
                        className="h-12 bg-slate-50 border-transparent rounded-xl"
                        invalid={!!err("shipping_address.state")}
                      />
                    </div>
                    <Field label="Zipcode *" ek="shipping_address.zipcode" err={err} cls={inputCls}>
                      <Input className={inputCls("shipping_address.zipcode")} value={form.shipping_address.zipcode}
                        inputMode={form.shipping_address.country === "US" ? "numeric" : "text"}
                        maxLength={postalCodeMaxLen(form.shipping_address.country)}
                        onChange={(e) => setAddr({ zipcode: sanitizePostalCode(e.target.value, form.shipping_address.country) })} />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Nothing to save while an untouched saved address is selected. */}
              {selectedAddrId === null || selectedAddrId === NEW_ADDR || addrTouched ? (
                <label className="flex items-center gap-2 pt-3 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-slate-700"
                    checked={saveToBook}
                    onChange={(e) => setSaveToBook(e.target.checked)}
                  />
                  Save this address to my address book
                </label>
              ) : null}

              <div className="flex items-center justify-between pt-2">
                <button onClick={() => navigate(`${basePath}/shop`)} className="text-sm text-slate-500 hover:text-slate-800">‹ Continue Shopping</button>
                <Button className="text-white px-8 h-12 rounded-xl" style={{ backgroundColor: primary }} onClick={proceedFromAddress}>Proceed</Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Step 2 — Delivery Options */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          {stepHeader(2, "Delivery Options")}
          {activeStep === 2 ? (
            <div className="p-6 space-y-4">
              <RadioGroup value={shippingId} onValueChange={setShippingId} className="space-y-2">
                {(charges.shipping_methods || []).map((s) => {
                  const cost = shippingFor(s, totals.subtotal);
                  const sel = shippingId === s.id;
                  return (
                    <label key={s.id} className={cn("flex items-center justify-between rounded-xl border p-4 cursor-pointer", sel && "ring-1")}
                      style={sel ? { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` } : undefined}>
                      <span className="flex items-center gap-3 text-sm"><RadioGroupItem value={s.id} /> {s.name}</span>
                      <span className={cn("text-sm font-semibold", cost === 0 && "text-green-600")}>{cost === 0 ? "Free" : money(cost)}</span>
                    </label>
                  );
                })}
              </RadioGroup>
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setActiveStep(1)} className="text-sm text-slate-500 hover:text-slate-800">‹ Back to Address</button>
                <Button className="text-white px-8 h-12 rounded-xl" style={{ backgroundColor: primary }} disabled={!shippingId || processing} onClick={goToPayment}>
                  {processing ? "Loading Payment…" : "Next"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Step 3 — Pay with Stripe (inline) */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          {stepHeader(3, null, <>Pay with <span style={{ color: primary }}>Stripe</span></>)}
          {activeStep === 3 ? (
            <div className="p-6">
              {!stripePublishableKey ? (
                <p className="text-sm text-red-500">Online payment is not configured. Please contact support.</p>
              ) : clientSecret && stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripePayment clientSecret={clientSecret} formData={form} totalAmount={serverTotal} onSuccess={onPaid} onError={onDeclined} />
                </Elements>
              ) : (
                <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Initializing secure payment…</div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Your Order summary */}
      <div className="lg:col-span-4">
        <div className="sticky top-24 space-y-4">
          <h2 className="text-xl font-bold text-slate-900">Your Order</h2>
          <div className="bg-white rounded-3xl border border-slate-100 p-6 space-y-5">
            <div className="space-y-4 max-h-[360px] overflow-y-auto pr-1">
              {cart.map((it) => (
                <div key={it.key} className="flex gap-3 group">
                  <div className="w-16 h-16 bg-slate-50 rounded-xl overflow-hidden shrink-0 border border-slate-100 flex items-center justify-center">
                    {it.product.image_url ? <img src={getImageUrl(it.product.image_url)} alt={it.product.name} className="w-full h-full object-contain" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-bold text-slate-900 truncate">{it.product.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400 font-medium">Qty: {it.quantity}</span>
                      <span className="text-[10px] font-bold" style={{ color: primary }}>{money(unitPrice(it))}</span>
                    </div>
                    {(it.customizations || []).map((c) => (
                      <p key={c.customization_id} className="text-[10px] text-slate-400 truncate">{c.name}: {c.fields.map((f) => f.value).filter((v) => v && v !== true).join(", ")}</p>
                    ))}
                    {it.engraving_lines?.length ? <p className="text-[10px] text-slate-400 truncate">Engraving: {it.engraving_lines.join(" / ")}</p> : null}
                  </div>
                  <button onClick={() => removeItem(it.key)} className="text-slate-300 hover:text-red-500 h-fit"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
              <Row label="Subtotal" value={money(totals.subtotal)} />
              <Row label="Shipping" value={totals.shipping === 0 ? "Free" : money(totals.shipping)} valueClass={totals.shipping === 0 ? "text-green-600" : ""} />
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: primary }}>Total Payable</span>
                <span className="text-xl font-black text-slate-900">{money(totals.total)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-2xl border" style={{ background: "#EAF3FC", borderColor: "#CFE3F8" }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: primary }}><ShieldCheck size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-800">Secure Checkout</p>
              <p className="text-[11px] text-slate-500">Your data is always protected by Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, ek, err, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</Label>
      {children}
      {err(ek) ? <p className="text-[10px] text-red-500 font-bold">{err(ek)}</p> : null}
    </div>
  );
}
function Row({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn("font-semibold text-slate-800", valueClass)}>{value}</span>
    </div>
  );
}
